import {useLazyQuery} from "@apollo/client";
import debounce from "lodash/debounce";
import {useEffect, useMemo, useRef, useState} from "react";
import {components, GroupBase, MultiValue, OptionProps} from "react-select";

import {Select} from "@/components/select/Select.tsx";
import {CheckInfo, FindChecksResult, GQL_FIND_CHECKS} from "@/lib/queries/statuspages.ts";
import {MetricsExplorerSchema} from "@/lib/types/metricsExplorer.ts";
import {groupChecksByType} from "@/pages/metricsExplorer/schemaUtils.ts";

interface CheckMultiSelectProps {
  /** Ties the picker to its FormLabel, which insists on a real control id. */
  inputId: string;
  value: number[];
  onChange: (checkIds: number[]) => void;
  /** Called with the full records whenever ids are resolved, however they arrived. */
  onChecksResolved: (checks: CheckInfo[]) => void;
  schema: MetricsExplorerSchema | undefined;
  /** Types of the checks already picked, which decide how the results are sectioned. */
  checkTypes: string[];
}

const CheckOption = ({data, ...props}: OptionProps<CheckInfo, true>) => (
  <components.Option {...props} data={data}>
    <div className="text-truncate">{data.name}</div>
    <div className="pulse-color-gray-600 pulseTextBodyTiny">{data.address}</div>
  </components.Option>
);

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 50;

/**
 * Multi-check picker for one builder row, sectioned by check type.
 *
 * `ServiceSelect` covers the same search but is typed single-select and has a dozen
 * callers, so it is composed from `Select` here rather than made generic in place. The
 * results are kept here rather than inside an async select so the sections re-sort the
 * moment a check is picked, not on the next search.
 */
export const CheckMultiSelect = ({
  inputId,
  value,
  onChange,
  onChecksResolved,
  schema,
  checkTypes,
}: CheckMultiSelectProps) => {
  const [searchChecks, {loading}] = useLazyQuery<FindChecksResult>(GQL_FIND_CHECKS);
  const [selected, setSelected] = useState<CheckInfo[]>([]);
  const [results, setResults] = useState<CheckInfo[]>([]);
  const requestedInput = useRef<string | null>(null);

  // A config restored from the URL or a preset link carries ids but no names. Resolve them
  // so the chips read as check names, and hand the records up: the rail needs each check's
  // type to narrow the metric catalog, and without it no data type or metric can be picked.
  useEffect(() => {
    const missing = value.filter((id) => !selected.some((check) => check.id === id));
    if (missing.length === 0) {
      return;
    }
    searchChecks({variables: {search: "", limit: missing.length, include: missing}}).then(({data}) => {
      const found = data?.findChecks?.items ?? [];
      if (found.length === 0) {
        return;
      }
      setSelected((previous) => [
        ...previous,
        ...found.filter((check) => !previous.some((existing) => existing.id === check.id)),
      ]);
      onChecksResolved(found);
    });
  }, [value, selected, searchChecks, onChecksResolved]);

  const runSearch = useMemo(
    () =>
      debounce((input: string) => {
        searchChecks({variables: {search: input, limit: SEARCH_LIMIT}}).then(({data}) => {
          // An older, slower search must not overwrite the results of the one typed after it.
          if (requestedInput.current === input) {
            setResults(data?.findChecks?.items ?? []);
          }
        });
      }, SEARCH_DEBOUNCE_MS),
    [searchChecks]
  );

  useEffect(() => {
    requestedInput.current = "";
    runSearch("");
    runSearch.flush();
    return () => runSearch.cancel();
  }, [runSearch]);

  const groups = useMemo(() => groupChecksByType(results, schema, checkTypes), [results, schema, checkTypes]);

  // Picking a check or leaving the field clears the typed text, and the list should follow.
  const handleInputChange = (input: string) => {
    if (input !== requestedInput.current) {
      requestedInput.current = input;
      runSearch(input);
    }
  };

  const handleChange = (next: MultiValue<CheckInfo>) => {
    const checks = [...next];
    setSelected(checks);
    onChecksResolved(checks);
    onChange(checks.map((check) => check.id));
  };

  return (
    <Select<CheckInfo, true, GroupBase<CheckInfo>>
      inputId={inputId}
      isMulti
      options={groups}
      // The server has already matched the search; filtering again here would only drop
      // checks it matched on something other than the name.
      filterOption={null}
      onInputChange={handleInputChange}
      isLoading={loading}
      value={selected.filter((check) => value.includes(check.id))}
      onChange={handleChange}
      getOptionValue={(check) => String(check.id)}
      getOptionLabel={(check) => check.name}
      components={{Option: CheckOption}}
      placeholder="Select checks…"
      size="small"
    />
  );
};
